import {
    Box,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';

const HEADER_COLUMNS = [
    { label: 'Listing', flex: '2 1 280px', minWidth: 200 },
    { label: 'Location', flex: '1.2 1 180px', minWidth: 140 },
    { label: 'Capacity', flex: '0 0 120px', minWidth: 100 },
    { label: 'Price', flex: '0 0 150px', minWidth: 120 },
    { label: 'Booked', flex: '1 1 160px', minWidth: 120 },
    { label: 'Actions', flex: '0 0 200px', minWidth: 180 },
] as const;

function ListingRowSkeleton() {
    return (
        <Stack direction='row' spacing={1.25} alignItems='center'>
            <Skeleton
                variant='rounded'
                width={64}
                height={48}
                sx={{ borderRadius: 1, flexShrink: 0 }}
            />
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant='text' width='72%' height={18} />
                <Skeleton variant='text' width='55%' height={16} />
                <Skeleton variant='text' width='40%' height={16} />
                <Skeleton variant='text' width='48%' height={16} />
            </Stack>
        </Stack>
    );
}

function TableRowSkeleton() {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.25,
                borderBottom: (t) =>
                    `1px solid ${t.palette.custom.lightBorderColor}`,
            }}
        >
            <Box sx={{ flex: '2 1 280px', minWidth: 200 }}>
                <ListingRowSkeleton />
            </Box>
            <Box sx={{ flex: '1.2 1 180px', minWidth: 140 }}>
                <Skeleton variant='text' width='85%' height={20} />
            </Box>
            <Box sx={{ flex: '0 0 120px', minWidth: 100 }}>
                <Skeleton variant='text' width='70%' height={20} />
            </Box>
            <Box sx={{ flex: '0 0 150px', minWidth: 120 }}>
                <Skeleton variant='text' width='80%' height={20} />
            </Box>
            <Box sx={{ flex: '1 1 160px', minWidth: 120 }}>
                <Skeleton variant='text' width='75%' height={20} />
            </Box>
            <Box
                sx={{
                    flex: '0 0 200px',
                    minWidth: 180,
                    display: 'flex',
                    gap: 1,
                }}
            >
                <Skeleton
                    variant='rounded'
                    width={72}
                    height={32}
                    sx={{ borderRadius: 1.5 }}
                />
                <Skeleton
                    variant='rounded'
                    width={96}
                    height={32}
                    sx={{ borderRadius: 1.5 }}
                />
            </Box>
        </Box>
    );
}

function MobileCardSkeleton() {
    return (
        <Box
            sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
            }}
        >
            <Skeleton variant='rectangular' sx={{ width: '100%', pt: '75%' }} />
            <Stack spacing={1} sx={{ p: 2 }}>
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    spacing={1}
                >
                    <Skeleton variant='text' width='55%' height={28} />
                    <Skeleton variant='text' width='30%' height={20} />
                </Stack>
                <Skeleton variant='text' width='70%' height={18} />
                <Skeleton variant='text' width='45%' height={18} />
                <Skeleton variant='text' width='40%' height={18} />
                <Stack direction='row' spacing={1} sx={{ pt: 0.5 }}>
                    <Skeleton
                        variant='rounded'
                        height={32}
                        sx={{ borderRadius: 1.5, flex: 1 }}
                    />
                    <Skeleton
                        variant='rounded'
                        height={32}
                        sx={{ borderRadius: 1.5, flex: 1 }}
                    />
                </Stack>
            </Stack>
        </Box>
    );
}

export default function OwnerListingsTableLoadingSkeleton({
    rowCount = 5,
}: {
    rowCount?: number;
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (isMobile) {
        return (
            <Stack spacing={2} sx={{ width: '100%' }}>
                {Array.from({ length: Math.min(rowCount, 3) }).map(
                    (_, index) => (
                        <MobileCardSkeleton key={index} />
                    ),
                )}
            </Stack>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1,
                    borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    bgcolor: 'background.paper',
                }}
            >
                {HEADER_COLUMNS.map((column) => (
                    <Box
                        key={column.label}
                        sx={{
                            flex: column.flex,
                            minWidth: column.minWidth,
                        }}
                    >
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 700, color: 'text.primary' }}
                        >
                            {column.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {Array.from({ length: rowCount }).map((_, index) => (
                <TableRowSkeleton key={index} />
            ))}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    px: 2,
                    py: 1.25,
                    borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
                }}
            >
                <Skeleton variant='text' width={180} height={20} />
                <Skeleton variant='rounded' width={280} height={32} />
                <Skeleton variant='rounded' width={100} height={32} />
            </Box>
        </Box>
    );
}
